import React from 'react';

export function PlayerPanel({
  player,
  piecesPlaced,
  maxPieces,
  isActive,
  compact,
  displayName,
  characterImageSrc,
  showTurnTimer,
  turnCountdownPercent,
  turnSecondsLeft,
}) {
  const placed = piecesPlaced[player] ?? 0;
  const max = maxPieces[player] ?? 4;
  const remaining = Math.max(0, max - placed);
  const showTimer = Boolean(isActive && showTurnTimer);
  const progress = showTimer ? (turnCountdownPercent ?? 100) : 0;

  const slots = Array.from({ length: max }, (_, i) => {
    const isOnBoard = i < placed;
    return (
      <span
        key={i}
        className={`game-hud__piece-slot game-hud__piece-slot--${player.toLowerCase()} ${
          isOnBoard ? 'game-hud__piece-slot--used' : ''
        }`}
        aria-hidden="true"
      >
        {player}
      </span>
    );
  });

  return (
    <div
      className={`game-hud__panel game-hud__panel--${player.toLowerCase()} ${
        isActive ? 'game-hud__panel--active' : ''
      } ${compact ? 'game-hud__panel--compact' : ''}`}
    >
      {displayName ? (
        <span className="game-hud__player-name">
          {characterImageSrc ? (
            <img
              src={characterImageSrc}
              alt=""
              className="game-hud__player-character-img"
            />
          ) : null}
          <span className="game-hud__player-name-text">{displayName}</span>
        </span>
      ) : null}
      <div className="game-hud__pieces-row">{slots}</div>
      <div
        className={`game-hud__progress-track game-hud__progress-track--timer${
          showTimer ? ' game-hud__progress-track--active' : ''
        }`}
        role={showTimer ? 'timer' : undefined}
        aria-label={showTimer ? 'Turn countdown' : undefined}
        aria-valuenow={showTimer ? turnSecondsLeft : undefined}
        aria-valuemin={showTimer ? 0 : undefined}
        aria-valuemax={showTimer ? 10 : undefined}
      >
        <div
          className={`game-hud__progress-fill game-hud__progress-fill--${player.toLowerCase()}${
            showTimer ? ' game-hud__progress-fill--countdown' : ''
          }`}
          style={{ width: `${progress}%` }}
        />
      </div>
      <span className="game-hud__remaining">
        {showTimer
          ? `${turnSecondsLeft}s · Turn`
          : `${remaining} left${isActive ? ' · Turn' : ''}`}
      </span>
    </div>
  );
}

function useHudPanelState({
  gameState,
  isAIThinking,
  isLocalPvp,
  playerRole,
  passPlayNames,
  xDisplayName,
  oDisplayName,
  xCharacterImageSrc,
  oCharacterImageSrc,
  showTurnTimer,
  turnCountdownPercent,
  turnSecondsLeft,
}) {
  if (!gameState) return null;

  const { piecesPlaced, maxPieces } = gameState;
  const xActive =
    gameState.currentPlayer === 'X' &&
    !gameState.winner &&
    !gameState.isDraw &&
    !isAIThinking;
  const oActive =
    gameState.currentPlayer === 'O' &&
    !gameState.winner &&
    !gameState.isDraw &&
    !isAIThinking;

  return {
    piecesPlaced,
    maxPieces,
    x: {
      isActive: xActive && (isLocalPvp || playerRole === 'X'),
      displayName:
        xDisplayName ?? (isLocalPvp ? passPlayNames?.xName : undefined),
      characterImageSrc: xCharacterImageSrc,
      showTurnTimer:
        showTurnTimer && xActive && (isLocalPvp || playerRole === 'X'),
    },
    o: {
      isActive: oActive && (isLocalPvp || playerRole === 'O'),
      displayName:
        oDisplayName ?? (isLocalPvp ? passPlayNames?.oName : undefined),
      characterImageSrc: oCharacterImageSrc,
      showTurnTimer:
        showTurnTimer && oActive && (isLocalPvp || playerRole === 'O'),
    },
    turnCountdownPercent,
    turnSecondsLeft,
  };
}

export function GameHudXPanel(props) {
  const state = useHudPanelState(props);
  if (!state) return null;

  return (
    <div className="game-page__hud-slot game-page__hud-slot--top">
      <PlayerPanel
        player="X"
        piecesPlaced={state.piecesPlaced}
        maxPieces={state.maxPieces}
        isActive={state.x.isActive}
        compact
        displayName={state.x.displayName}
        characterImageSrc={state.x.characterImageSrc}
        showTurnTimer={state.x.showTurnTimer}
        turnCountdownPercent={state.turnCountdownPercent}
        turnSecondsLeft={state.turnSecondsLeft}
      />
    </div>
  );
}

export function GameHudOPanel(props) {
  const state = useHudPanelState(props);
  if (!state) return null;

  return (
    <div className="game-page__hud-slot game-page__hud-slot--bottom">
      <PlayerPanel
        player="O"
        piecesPlaced={state.piecesPlaced}
        maxPieces={state.maxPieces}
        isActive={state.o.isActive}
        compact
        displayName={state.o.displayName}
        characterImageSrc={state.o.characterImageSrc}
        showTurnTimer={state.o.showTurnTimer}
        turnCountdownPercent={state.turnCountdownPercent}
        turnSecondsLeft={state.turnSecondsLeft}
      />
    </div>
  );
}
